import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, CheckCircle, User, Trash2, Shield } from "lucide-react";

const TRUST_KEYWORDS = [
  'TRUST', 'TRUSTEE', 'TRUSTEES', 'REVOCABLE', 'IRREVOCABLE',
  'LIVING TRUST', 'FAMILY TRUST',
];

interface TrustRecord {
  id: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  county: string | null;
  origin_state: string | null;
  fullName: string;
  suggestedFirstName: string;
  suggestedLastName: string;
}

interface TrusteeReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

function extractTrusteeName(businessName: string): { first: string; last: string } {
  const upper = businessName.toUpperCase();

  // Pattern: "JOHN SMITH REVOCABLE TRUST" or "JOHN & JANE SMITH TRUST"
  // Try to extract name before trust keyword
  const trustIdx = upper.search(/\b(REVOCABLE|IRREVOCABLE|LIVING|FAMILY)?\s*TRUST(EE)?S?\b/);
  if (trustIdx > 0) {
    const namePart = businessName.substring(0, trustIdx).trim();
    // Remove trailing connectors like "THE", "&", "AND"
    const cleaned = namePart.replace(/\b(THE|AND|&)\s*$/i, '').trim();
    const words = cleaned.split(/\s+/);
    if (words.length >= 2) {
      const last = words[words.length - 1];
      const first = words.slice(0, -1).join(' ');
      return { first: toTitleCase(first), last: toTitleCase(last) };
    }
    if (words.length === 1) {
      return { first: '', last: toTitleCase(words[0]) };
    }
  }

  return { first: '', last: '' };
}

function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

export function TrusteeReviewDialog({ open, onOpenChange, onComplete }: TrusteeReviewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<TrustRecord[]>([]);
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, { first: string; last: string }>>({});

  useEffect(() => {
    if (open) scanForTrusts();
  }, [open]);

  const scanForTrusts = async () => {
    setLoading(true);
    try {
      let allRecords: any[] = [];
      let page = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("mailing_records")
          .select("id, first_name, last_name, business_name, county, origin_state")
          .not("business_name", "is", null)
          .neq("business_name", "")
          .range(page * pageSize, (page + 1) * pageSize - 1);
        if (error || !data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        page++;
      }

      const trusts: TrustRecord[] = [];
      for (const r of allRecords) {
        const bn = (r.business_name || '').toUpperCase();
        const isTrust = TRUST_KEYWORDS.some(kw => {
          const regex = new RegExp(`\\b${kw.replace(/\./g, '\\.')}\\b`, 'i');
          return regex.test(bn);
        });
        if (!isTrust) continue;

        const suggested = extractTrusteeName(r.business_name || '');
        trusts.push({
          ...r,
          fullName: r.business_name || '',
          suggestedFirstName: suggested.first,
          suggestedLastName: suggested.last,
        });
      }

      setRecords(trusts);
      // Initialize edits with suggested names
      const initialEdits: Record<string, { first: string; last: string }> = {};
      trusts.forEach(t => {
        initialEdits[t.id] = { first: t.suggestedFirstName, last: t.suggestedLastName };
      });
      setEdits(initialEdits);

      if (trusts.length === 0) {
        toast.info("No trust entities found in business records");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to scan records");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTrustee = async (record: TrustRecord) => {
    const edit = edits[record.id];
    if (!edit?.last) {
      toast.error("Last name is required");
      return;
    }

    setProcessing(prev => new Set(prev).add(record.id));
    try {
      const { error } = await supabase
        .from("mailing_records")
        .update({
          first_name: edit.first || null,
          last_name: edit.last + ', Trustee',
          business_name: null,
        })
        .eq("id", record.id);

      if (error) throw error;
      toast.success(`Set "${edit.first} ${edit.last}, Trustee"`);
      setRecords(prev => prev.filter(r => r.id !== record.id));
    } catch {
      toast.error("Failed to update record");
    } finally {
      setProcessing(prev => { const n = new Set(prev); n.delete(record.id); return n; });
    }
  };

  const handleSkip = (id: string) => {
    setRecords(prev => prev.filter(r => r.id !== id));
  };

  const handleDelete = async (record: TrustRecord) => {
    setProcessing(prev => new Set(prev).add(record.id));
    try {
      const { error } = await supabase
        .from("mailing_records")
        .delete()
        .eq("id", record.id);
      if (error) throw error;
      toast.success(`Deleted "${record.fullName}"`);
      setRecords(prev => prev.filter(r => r.id !== record.id));
    } catch {
      toast.error("Failed to delete record");
    } finally {
      setProcessing(prev => { const n = new Set(prev); n.delete(record.id); return n; });
    }
  };

  const handleApplyAll = async () => {
    const toProcess = records.filter(r => edits[r.id]?.last);
    if (toProcess.length === 0) return;

    let successCount = 0;
    for (const record of toProcess) {
      const edit = edits[record.id];
      try {
        const { error } = await supabase
          .from("mailing_records")
          .update({
            first_name: edit.first || null,
            last_name: edit.last + ', Trustee',
            business_name: null,
          })
          .eq("id", record.id);
        if (!error) successCount++;
      } catch { /* skip */ }
    }
    toast.success(`Applied trustee name to ${successCount} records`);
    setRecords(prev => prev.filter(r => !edits[r.id]?.last));
  };

  const handleClose = () => {
    onOpenChange(false);
    if (records.length === 0) onComplete();
  };

  const updateEdit = (id: string, field: 'first' | 'last', value: string) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(o); }}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Review Trust Entities — Extract Trustee Names
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            These business records contain trust keywords. Edit the extracted name and apply as "First Last, Trustee".
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Scanning trust records...</span>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-12 w-12 text-primary mx-auto mb-3" />
            <p className="font-medium">All clear!</p>
            <p className="text-sm text-muted-foreground">No trust entities to review.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <Badge variant="secondary">{records.length} trust records</Badge>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRecords([])}>
                  Dismiss All
                </Button>
                <Button variant="default" size="sm" onClick={handleApplyAll}>
                  <Shield className="h-4 w-4 mr-1" />
                  Apply All as Trustee
                </Button>
              </div>
            </div>

            <div className="overflow-auto flex-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Name</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Last Name</TableHead>
                    <TableHead>County</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map(record => {
                    const edit = edits[record.id] || { first: '', last: '' };
                    return (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium text-sm max-w-[250px]">
                          <span className="break-words">{record.fullName}</span>
                          {record.first_name && (
                            <span className="text-xs text-muted-foreground block">
                              Current: {record.first_name} {record.last_name}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-8 text-sm w-[120px]"
                            value={edit.first}
                            onChange={(e) => updateEdit(record.id, 'first', e.target.value)}
                            placeholder="First"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-8 text-sm w-[120px]"
                            value={edit.last}
                            onChange={(e) => updateEdit(record.id, 'last', e.target.value)}
                            placeholder="Last"
                          />
                        </TableCell>
                        <TableCell className="text-sm">{record.county || '—'}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSkip(record.id)}
                            disabled={processing.has(record.id)}
                          >
                            Skip
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleApplyTrustee(record)}
                            disabled={processing.has(record.id) || !edit.last}
                          >
                            {processing.has(record.id) ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <User className="h-3.5 w-3.5 mr-1" />
                                Apply
                              </>
                            )}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(record)}
                            disabled={processing.has(record.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
